import { IconButton, Tooltip, Typography } from "@mui/material"
import MuiInput from "@shared/ui/mui-input/MuiInput"
import { UseFieldArrayReturn, UseFormReturn, Path } from "react-hook-form";
import { DocumentPaymentForm } from "../../../../models/document-payment";

// local data
import { paymentFieldsStructure } from "@features/documents/data/paymentFieldsStructure";
import { DocumentStructure } from "../../../../models/document-structure";
import { Account } from "../../../../models/account";
import { useEffect } from "react";
import { PaymentsFormType } from "../../pages/add-document/AddDocument.types";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from "@emotion/react";
import ClearIcon from '@mui/icons-material/Clear';


interface PaymentsProps {

    method: "add" | "edit";

    paymentsForm: UseFormReturn<{ payments: DocumentPaymentForm[] }>;
    paymentsFieldArray: UseFieldArrayReturn<{ payments: DocumentPaymentForm[] }, "payments", "id">;

    kupaAccounts: Account[];

    calcDocument: () => void;

    isDocumentClosed?: boolean;

}


function Payments({ method, paymentsForm, paymentsFieldArray, kupaAccounts, calcDocument, isDocumentClosed }: PaymentsProps) {

    const theme = useTheme();



    //update payment line when one field in the line changes.
    useEffect(() => {

        const subscription = paymentsForm.watch((_, { name }) => {

            if (name) {
                updatePaymentRow(name);
            }

            // todo: check if this line necessary:
            calcDocument();
        });

        return () => subscription.unsubscribe();

    }, [paymentsForm.watch()]);


    // update payment row after one field changed
    function updatePaymentRow(name: Path<PaymentsFormType>) {

        const row: number = Number(name.split('.')[1]);
        const fieldName = name.split('.').slice(2).join('.');

        //reset payment row fields if payment method changed
        if (fieldName === 'kupa_account_id') {

            const payments = paymentsForm.getValues('payments');
            const kupa = kupaAccounts?.find(kupa => kupa.id === payments[row].kupa_account_id);

            if (kupa?.payment_type) {
                payments[row] = {
                    total: payments[row].total,
                    kupa_account_id: payments[row].kupa_account_id,
                    payment_method: kupa.payment_type
                };

                paymentsForm.reset({ payments: payments });
            }

        }
    }


    //add a new payment line
    function addPaymentLine() {

        paymentsForm.reset({ payments: [...paymentsForm.getValues().payments, { total: 0 }] });

    }


    function disablePaymentField(field: DocumentStructure, row: number): boolean {

        const kupa_id = paymentsForm.getValues("payments")[row]["kupa_account_id"];

        const arrayForDefualt = ["total", "kupa_account_id"];
        const arrayForCheck = ["total", "kupa_account_id", "bank_account_number", "bank_branch_number", "bank_name", "bank_number", "check_number", "to_date", "tax"];
        const arrayForCard = ["total", "kupa_account_id", "to_date", "tax"];
        const arrayForBankTransfer = ["total", "kupa_account_id", "bank_account_id"];

        if (kupa_id) {
            const kupa = kupaAccounts.find(kupa => kupa.id === kupa_id);


            if (kupa?.payment_type === "שיקים") {

                if (!arrayForCheck.includes(field.field_name)) {
                    return true;
                }

                else {
                    return false;
                }
            }

            if (kupa?.payment_type === "מזומן") {

                if (!arrayForDefualt.includes(field.field_name)) {
                    return true;
                }

                else {
                    return false;
                }
            }

            if (kupa?.payment_type === "אשראי") {

                if (!arrayForCard.includes(field.field_name)) {
                    return true;
                }

                else {
                    return false;
                }
            }

            if (kupa?.payment_type === "העברות") {

                if (!arrayForBankTransfer.includes(field.field_name)) {
                    return true;
                }

                else {
                    return false;
                }
            }
        }

        //defualt
        else {
            if (!arrayForDefualt.includes(field.field_name)) {
                return true;
            }

            else {
                return false;
            }
        }

        return false;

    }

    
    //deleting a row by specify an index and row type
    function deleteRow(index: number): void {
        paymentsFieldArray.remove(index);
        calcDocument();
    }


    if (method === "add") {
        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            תשלום:
                        </Typography>
                        <Tooltip title="הוספת שורה" placement="top">
                            <span>
                                <IconButton onClick={addPaymentLine} disabled={isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <ExpandMoreIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>

                <table>
                    <tbody>
                        {
                            paymentsFieldArray.fields.map((field, rowIndex) => (

                                <tr key={field.id}>
                                    <td className="row-number">{rowIndex + 1}.</td>
                                        {
                                            paymentFieldsStructure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => {
                                                return (field.type === "תשלום") ? 
                                                    <td key={field.field_name}>
                                                        <MuiInput field={field} variant='outlined' useForm={paymentsForm} prefix={`payments.${rowIndex}.`} showRequired disabled={disablePaymentField(field, rowIndex)} />
                                                    </td> : null
                                            })
                                        }
                                    <td className="delete-row">
                                        <Tooltip title="הסרת שורה" placement="top">
                                            <span>
                                                <IconButton onClick={()=>{deleteRow(rowIndex)}} sx={{ color: theme.colors.main_color }}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </td>
                                </tr>

                            ))
                        }
                    </tbody>
                </table>
            </>
        )
    }

    else if (method === "edit") {
        return (
            <>
                <div className={`title ${isDocumentClosed? "disabled" : ""}`}>
                    <div>
                        <Typography variant="body1" component="span">
                            תשלום:
                        </Typography>
                        <Tooltip title="הוספת שורה" placement="top">
                            <span>
                                <IconButton onClick={addPaymentLine} disabled={isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <ExpandMoreIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>

                <table className={isDocumentClosed? "disabled" : ""}>
                    <tbody>
                        {
                            paymentsFieldArray.fields.map((field, rowIndex) => (

                                <tr key={field.id}>
                                    <td className="row-number">{rowIndex + 1}.</td>
                                        {
                                            paymentFieldsStructure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => {
                                                return (field.type === "תשלום") ? 
                                                    <td key={field.field_name}>
                                                        <MuiInput field={field} variant='outlined' useForm={paymentsForm} prefix={`payments.${rowIndex}.`} showRequired disabled={disablePaymentField(field, rowIndex) || isDocumentClosed} />
                                                    </td> : null
                                            })
                                        }
                                    <td className="delete-row">
                                        <Tooltip title="הסרת שורה" placement="top">
                                            <span>
                                                <IconButton onClick={()=>{deleteRow(rowIndex)}} sx={{ color: theme.colors.main_color }} disabled={isDocumentClosed}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </td>
                                </tr>

                            ))
                        }
                    </tbody>
                </table>
            </>
        )
    }

}

export default Payments